import { notfallStellen, notrufLuecken } from '../bestandUebernahme';
import type { NotrufplanInhalt } from '../../sige-plan/types';
import type { AsplanBundle } from '../types';

/**
 * Welche Aushaenge dieser Plan hergibt — und was ihnen noch fehlt.
 *
 * Bewusst NEBEN den Komponenten und nicht in derselben Datei: eine Datei, die
 * Komponenten UND Funktionen ausfuehrt, verliert im Entwicklungsbetrieb das
 * Fast Refresh (und der Lint dieses Projekts laeuft mit `--max-warnings 0`).
 *
 * ═══ WARUM DER ORT MITGEFUEHRT WIRD ═══════════════════════════════════════
 * Der haeufigste Fehler beim Aushaengen ist nicht das fehlende Blatt, sondern
 * das Blatt am falschen Platz: alles haengt am Bauzaun, und im Container
 * haengt nichts. Ein Schutzklassenblatt gehoert an den Zugang GENAU DIESES
 * Bereichs, die Schleusenordnung in die Schleuse, der Notfallausweis
 * ueberhaupt nicht an die Wand — er wird ausgegeben.
 */

/**
 * Ein Aushang OHNE die Aussage, was ihm fehlt.
 *
 * Diese Aussage haengt am Notrufplan des SiGe-Plans. Wer ihn nicht hat, kann
 * sie nicht treffen — und ein still aus `null` gerechnetes `fehlt` ist
 * schlimmer als ein fehlendes: es behauptet „im Notrufplan ist keine
 * Notfallstelle hinterlegt“, wo in Wahrheit nur der Aufrufer den Plan nicht
 * gelesen hat. Deshalb steht die Luecke in einem eigenen Typ.
 */
export interface AushangBlatt {
    key: string;
    titel: string;
    /** Wo das Blatt hingehoert. */
    ort: string;
}

export interface AushangEintrag extends AushangBlatt {
    /** Was fehlt, damit es vollstaendig ist. */
    fehlt?: string;
}

/**
 * Die Zeilen des Notrufkastens auf dem Blatt „Verhalten im Notfall".
 *
 * ═══ WARUM DAS EINE FUNKTION IST UND KEINE LISTE IN DER KOMPONENTE ════════
 * Die Reihenfolge IST die Aussage: 112 und 110 zuerst, dann die Giftnotruf-
 * zentrale, dann die geprueften Stellen aus dem Notrufplan, zuletzt die
 * baustelleneigenen Nummern. Wer im Ernstfall auf das Blatt sieht, liest von
 * oben. Diese Reihenfolge gehoert deshalb dorthin, wo sie pruefbar ist.
 *
 * `nummer` bleibt LEER, wenn keine hinterlegt ist — ausdruecklich kein
 * Gedankenstrich. Das Blatt macht daraus eine benannte Luecke; ein „—" saehe
 * aus wie eine geprueft-leere Angabe.
 *
 * Eine Notfallstelle ohne Telefonnummer faellt aus dem Kasten (sie steht mit
 * Anschrift auf dem Rettungskettenblatt): in einem Notrufkasten ist ein
 * Eintrag ohne Nummer nur Zeitverlust.
 */
export interface NotrufZeile {
    was: string;
    /** Leer heisst: im Plan nicht hinterlegt. */
    nummer: string;
    /** Von einem Menschen bestaetigt (`notfall_stellen`), nicht nur gefunden. */
    geprueft?: boolean;
    /** Name und Entfernung der Stelle, falls bekannt. */
    zusatz?: string;
}

export const notrufZeilen = (
    notrufplan: NotrufplanInhalt | null,
    notfall: Record<string, unknown> | null | undefined,
): NotrufZeile[] => {
    const wert = (k: string): string => String((notfall || {})[k] ?? '').trim();

    const zeilen: NotrufZeile[] = [
        { was: 'Rettungsdienst / Feuerwehr', nummer: '112' },
        { was: 'Polizei', nummer: '110' },
        { was: 'Giftnotruf', nummer: wert('giftnotruf') },
    ];

    for (const s of notfallStellen(notrufplan)) {
        if (!s.telefon) continue;
        zeilen.push({
            was: s.was,
            nummer: s.telefon,
            geprueft: s.geprueft,
            zusatz: [s.name, s.entfernung].filter(Boolean).join(' · '),
        });
    }

    zeilen.push({ was: 'Koordinator', nummer: wert('koordinator_tel') });
    zeilen.push({ was: 'Bauleitung', nummer: wert('bauleitung_tel') });

    return zeilen;
};

/**
 * Ob mindestens eine Notfallstelle von einem Menschen bestaetigt ist.
 *
 * „Geprueft" ist ein Bonus und kein Vorrang (so steht es schon am Notrufplan
 * des SiGe-Plans): eine frisch gesuchte Klinik ist besser als keine. Auf einem
 * ausgehaengten Blatt gehoert der Unterschied aber genannt — wer die Nummer im
 * Ernstfall waehlt, soll wissen, ob sie jemand angerufen hat.
 */
const keineGeprueft = (inhalt: NotrufplanInhalt | null): boolean => {
    const stellen = notfallStellen(inhalt);
    return stellen.length > 0 && stellen.every(s => !s.geprueft);
};

/** Was einem Blatt fehlt — als Funktion, damit die Antwort ohne Notrufplan
 *  gar nicht erst entstehen kann. */
type Luecke = (notrufplan: NotrufplanInhalt | null) => string | undefined;

/**
 * Die Blaetter dieses Plans — Schluessel, Titel, Ort.
 *
 * Ohne die Luecken: wer nur weiss, WELCHE Blaetter es gibt (die Ablage etwa,
 * die sie vormerken laesst), soll nicht nebenbei eine unbelegte Aussage
 * darueber mitbekommen, was ihnen fehlt.
 */
export const aushangBlaetterOhneLuecken = (b: AsplanBundle): AushangBlatt[] =>
    blaetter(b).map(({ key, titel, ort }) => ({ key, titel, ort }));

/**
 * @param notrufplan Der Notrufplan des SiGe-Plans — NUR gelesen, ohne
 * Vorgabewert. `null` heisst hier ausdruecklich „es ist keiner hinterlegt"
 * und nicht „der Aufrufer hat ihn nicht": den Unterschied kann diese Funktion
 * nicht sehen, deshalb muss ihn der Aufrufer treffen. Wer den Plan nicht hat,
 * nimmt `aushangBlaetterOhneLuecken`. Er wird ausdruecklich nicht in die
 * asplan-Tabellen kopiert (Plan Abschnitt 10: „lesen — EINE Quelle").
 */
export const verfuegbareAushaenge = (
    b: AsplanBundle,
    notrufplan: NotrufplanInhalt | null,
): AushangEintrag[] =>
    blaetter(b).map(({ key, titel, ort, luecke }) => ({
        key, titel, ort, fehlt: luecke?.(notrufplan),
    }));

const blaetter = (b: AsplanBundle): (AushangBlatt & { luecke?: Luecke })[] => {
    const liste: (AushangBlatt & { luecke?: Luecke })[] = [
        {
            key: 'notfall',
            titel: 'Verhalten im Notfall',
            ort: 'Schwarz-Weiß-Container, Baustelleneinrichtung',
            // Die Luecken kommen aus `notrufLuecken` und werden hier NICHT
            // nachgebaut: dieselbe Aussage an zwei Stellen zu formulieren
            // heisst, sie an einer zu pflegen.
            luecke: notrufplan => [
                b.plan.stoffe.filter(s => s.aktiv).length === 0
                    ? 'Es ist kein Gefahrstoff erfasst, das Blatt nennt dann keine.'
                    : '',
                ...notrufLuecken(notrufplan, b.plan.notfall),
            ].filter(Boolean).join(' ') || undefined,
        },
        {
            key: 'rettungskette',
            titel: 'Rettungskette und Notfallstellen',
            ort: 'im Container neben dem Notfallblatt und am Zugang zum Bereich',
            luecke: notrufplan => (notfallStellen(notrufplan).length === 0
                ? 'Im Notrufplan des SiGe-Plans ist keine Notfallstelle hinterlegt, '
                    + 'das Blatt nennt dann nur 112.'
                : keineGeprueft(notrufplan)
                    ? 'Keine der Notfallstellen ist bestätigt: sie sind gefunden, nicht '
                        + 'geprüft. Das Blatt weist das aus.'
                    : undefined),
        },
        {
            key: 'schleuse',
            titel: 'Schwarz-Weiß-Anlage: Ablauf',
            ort: 'in der Schleuse, sichtbar von Kammer 1',
        },
        {
            key: 'hygiene',
            titel: 'Verhaltensregeln im Schwarzbereich',
            ort: 'am Zugang zum Bereich und im Container',
        },
        {
            key: 'ausweis',
            titel: 'Notfallausweis (Vordruck)',
            ort: 'nicht aushängen, je Beschäftigtem ausgeben',
        },
    ];

    for (const c of b.cluster) {
        if (c.art === 'abgegrenzt') continue;
        liste.push({
            key: `sk_${c.kennung}`,
            titel: `Bereich ${c.kennung}: was hier zu tragen ist`,
            ort: `am Zugang zu Bereich ${c.kennung}`,
            // Haengt nicht am Notrufplan — steht deshalb auch ohne ihn fest.
            luecke: () => (c.schutzklasse_key
                ? undefined
                : 'Dem Bereich ist noch keine Schutzklasse zugeordnet.'),
        });
    }

    return liste;
};
